// ABOUTME: Component for Gene Set Enrichment Analysis (GSEA) using MAGMA
// ABOUTME: Provides file upload, endpoint selection, inline enrichment plots, and result download

import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { NgxSpinnerService } from 'ngx-spinner';
import { GseaService } from '../../services/gsea.service';
import {
  IGseaEndpoint,
  IGseaEndpointsResponse,
  IGseaAnalysisResponse,
} from '../../interfaces/i-gsea';

interface ValidationError {
  type: 'file' | 'endpoint' | 'format' | 'api';
  message: string;
}

@Component({
  selector: 'app-gsea-analysis',
  templateUrl: './gsea-analysis.component.html',
  styleUrl: './gsea-analysis.component.scss',
})
export class GseaAnalysisComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  // Endpoint data
  endpoints: IGseaEndpoint[] = [];
  selectedEndpoint: string = '';
  magmaAvailable: boolean = false;

  // File handling
  selectedFile: File | null = null;
  fileName: string = '';

  // State management
  isLoadingEndpoints: boolean = true;
  isAnalyzing: boolean = false;
  isDownloading: boolean = false;
  error: ValidationError | null = null;
  warnings: string[] = [];

  // Analysis results
  analysisResult: IGseaAnalysisResponse | null = null;

  // Significance threshold for visual labeling
  readonly significanceThreshold: number = 0.05;

  // File format help
  showFormatHelp: boolean = false;

  constructor(
    private gseaService: GseaService,
    private router: Router,
    private spinner: NgxSpinnerService,
  ) {}

  ngOnInit(): void {
    this.loadEndpoints();
  }

  loadEndpoints(): void {
    this.isLoadingEndpoints = true;
    this.error = null;

    this.gseaService.getEndpoints().subscribe({
      next: (response: IGseaEndpointsResponse) => {
        this.endpoints = response.endpoints;
        this.magmaAvailable = response.magma_available;
        this.isLoadingEndpoints = false;

        if (!this.magmaAvailable) {
          this.error = {
            type: 'api',
            message:
              'MAGMA analysis is currently unavailable. Please contact the administrator.',
          };
        }
      },
      error: (err: HttpErrorResponse) => {
        this.isLoadingEndpoints = false;
        this.error = {
          type: 'api',
          message: `Failed to load endpoints: ${err.message || 'Unknown error'}`,
        };
      },
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.validateAndSetFile(file);
    }
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.validateAndSetFile(event.dataTransfer.files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  private validateAndSetFile(file: File): void {
    this.error = null;
    this.analysisResult = null;

    // Validate file type (must be text)
    if (
      !file.type.includes('text') &&
      !file.name.endsWith('.txt') &&
      !file.name.endsWith('.tsv') &&
      !file.name.endsWith('.csv')
    ) {
      this.error = {
        type: 'file',
        message: 'Please upload a text file (.txt, .tsv, or .csv)',
      };
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      this.error = {
        type: 'file',
        message: 'File size exceeds 5MB limit',
      };
      return;
    }

    // Basic content validation (check first few lines)
    const reader = new FileReader();
    reader.onload = () => {
      const content = reader.result as string;
      const validationError = this.validateFileContent(content);

      if (validationError) {
        this.error = validationError;
        this.selectedFile = null;
        this.fileName = '';
      } else {
        this.selectedFile = file;
        this.fileName = file.name;
      }
    };
    reader.onerror = () => {
      this.error = {
        type: 'file',
        message: 'Failed to read file',
      };
    };
    reader.readAsText(file);
  }

  private validateFileContent(content: string): ValidationError | null {
    const lines = content.trim().split('\n');

    if (lines.length === 0 || (lines.length === 1 && lines[0].trim() === '')) {
      return { type: 'format', message: 'File is empty' };
    }

    // Check first line has at least 2 parts (set name + at least one gene)
    // Support tab, comma, or space delimiters
    const firstLine = lines[0];
    let parts: string[];

    if (firstLine.includes('\t')) {
      parts = firstLine.split('\t');
    } else if (firstLine.includes(',')) {
      parts = firstLine.split(',');
    } else {
      parts = firstLine.split(/\s+/);
    }

    // Filter empty parts
    parts = parts.map((p) => p.trim()).filter((p) => p.length > 0);

    if (parts.length < 2) {
      return {
        type: 'format',
        message:
          'Invalid format: Each line must have SET_NAME followed by gene identifiers (Ensembl IDs or gene symbols)',
      };
    }

    // Check that remaining parts look like gene identifiers
    // Accept Ensembl IDs (ENSG...) or gene symbols (alphanumeric, may include hyphens/underscores)
    const genePattern = /^(ENSG\d{11}|[A-Za-z][A-Za-z0-9_-]*)$/;
    const hasValidGene = parts.slice(1).some((part) => genePattern.test(part));

    if (!hasValidGene) {
      return {
        type: 'format',
        message:
          'No valid gene identifiers found. Use Ensembl IDs (ENSG00000XXXXXX) or gene symbols (e.g., MLH1, MSH3)',
      };
    }

    return null;
  }

  clearFile(): void {
    this.selectedFile = null;
    this.fileName = '';
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
    this.error = null;
    this.warnings = [];
    this.analysisResult = null;
    this.isDownloading = false;
  }

  canSubmit(): boolean {
    return (
      this.selectedFile !== null &&
      this.selectedEndpoint !== '' &&
      this.magmaAvailable &&
      !this.isAnalyzing
    );
  }

  submitAnalysis(): void {
    if (!this.canSubmit() || !this.selectedFile) {
      return;
    }

    this.isAnalyzing = true;
    this.error = null;
    this.warnings = [];
    this.analysisResult = null;
    this.spinner.show();

    this.gseaService
      .analyze(this.selectedEndpoint, this.selectedFile)
      .subscribe({
        next: (response: IGseaAnalysisResponse) => {
          this.isAnalyzing = false;
          this.spinner.hide();
          this.warnings = response.warnings;
          this.analysisResult = response;
        },
        error: (err: HttpErrorResponse) => {
          this.isAnalyzing = false;
          this.spinner.hide();

          this.error = {
            type: 'api',
            message: err.error?.detail || this.getErrorMessage(err.status),
          };
        },
      });
  }

  downloadResults(): void {
    if (!this.analysisResult) {
      return;
    }

    this.isDownloading = true;

    this.gseaService.downloadResults(this.analysisResult.result_id).subscribe({
      next: (blob: Blob) => {
        this.isDownloading = false;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `gsea_${this.analysisResult!.endpoint}_results.zip`;
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.isDownloading = false;
        this.error = {
          type: 'api',
          message:
            'Download failed. Results may have expired (10 minute TTL). Please run the analysis again.',
        };
      },
    });
  }

  isSignificant(pValue: number): boolean {
    return pValue < this.significanceThreshold;
  }

  getPlotSrc(plotB64: string): string {
    return `data:image/png;base64,${plotB64}`;
  }

  formatPValue(pValue: number): string {
    if (pValue < 0.001) {
      return pValue.toExponential(2);
    }
    return pValue.toFixed(4);
  }

  private getErrorMessage(status: number): string {
    switch (status) {
      case 400:
        return 'Invalid input. Please check your file format and selected endpoint.';
      case 401:
        return 'Authentication required. Please log in again.';
      case 500:
        return 'MAGMA analysis failed. Please try again or contact support.';
      case 503:
        return 'MAGMA service is temporarily unavailable. Please try again later.';
      default:
        return `An error occurred (${status}). Please try again.`;
    }
  }

  toggleFormatHelp(): void {
    this.showFormatHelp = !this.showFormatHelp;
  }

  goBack(): void {
    this.router.navigate(['/gwas-16']);
  }
}
